<template>
    <div>
        <app-template>

            <!-- En-tete -->
            <div class="flex items-start justify-between mb-8">
                <div>
                    <h1 class="text-3xl font-bold text-gray-900 tracking-tight">Bonjour 👋</h1>
                    <p class="text-sm text-gray-400 mt-1">Voici un résumé de votre activité du jour.</p>
                </div>
                <div class="flex items-center gap-2 text-sm text-gray-500 bg-white border border-gray-100 rounded-xl px-4 py-2.5 shadow-sm">
                    <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    {{ todayLabel }}
                </div>
            </div>

            <!-- KPIs -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div v-for="kpi in kpis" :key="kpi.key"
                     class="bg-white rounded-2xl border border-gray-100 px-5 py-4 shadow-sm flex items-center gap-4">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                         :class="kpi.iconBg">
                        <svg class="w-5 h-5" :class="kpi.iconColor" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="kpi.icon"/>
                        </svg>
                    </div>
                    <div>
                        <p class="text-xs text-gray-400 mb-0.5">{{ kpi.label }}</p>
                        <div v-if="loading" class="h-6 w-8 bg-gray-200 rounded"></div>
                        <div v-else class="flex items-baseline gap-2">
                            <p class="text-xl font-bold text-gray-900">{{ kpi.value }}</p>
                            <span v-if="kpi.sub" class="text-xs font-medium" :class="kpi.subColor">
                                {{ kpi.sub }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Grille principale -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

                <!-- Col gauche 2/3 -->
                <div class="lg:col-span-2 space-y-4">

                    <!-- Planning -->
                    <!-- <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <div class="flex items-center justify-between mb-5">
                            <h2 class="text-base font-semibold text-gray-900">Planning — 7 jours</h2>
                            <button @click="$router.push('/app/interventions')"
                                    class="text-xs text-gray-400 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors">
                                Voir tout
                            </button>
                        </div>

                        <div v-if="loading" class="grid grid-cols-7 gap-2">
                            <div v-for="i in 7" :key="i" class="h-24 rounded-xl bg-gray-100"></div>
                        </div>

                        <div v-else class="grid md:grid-cols-7 gap-2">
                            <div v-for="day in d.planning" :key="day.date"
                                 class="rounded-xl p-2.5 flex flex-col gap-1.5 min-h-24"
                                 :class="day.is_today ? 'bg-gray-900' : 'bg-gray-50 hover:bg-gray-100 transition-colors'">

                                <div class="text-center mb-0.5">
                                    <p class="text-xs font-medium capitalize"
                                       :class="day.is_today ? 'text-gray-400' : 'text-gray-400'">
                                        {{ day.label_short.split(' ')[0] }}
                                    </p>
                                    <p class="text-sm font-bold"
                                       :class="day.is_today ? 'text-white' : 'text-gray-700'">
                                        {{ day.label_short.split(' ')[1] || day.label_short }}
                                    </p>
                                </div>

                                <div v-if="day.count > 0" class="space-y-1">
                                    <div v-for="item in day.interventions.slice(0, 2)" :key="item.id"
                                         @click="$router.push(`/app/interventions?intervention_id=${item.id}`)"
                                         class="rounded-lg px-1.5 py-1 cursor-pointer"
                                         :class="day.is_today ? 'bg-white/10 hover:bg-white/20' : 'bg-white'"
                                         :style="!day.is_today ? { borderLeft: '2px solid ' + statusHex(item.status.color) } : {}">
                                        <p class="text-xs truncate font-medium leading-tight"
                                           :class="day.is_today ? 'text-gray-200' : 'text-gray-700'">
                                            {{ item.client }}
                                        </p>
                                        <p class="text-xs truncate"
                                           :class="day.is_today ? 'text-gray-500' : 'text-gray-400'">
                                            {{ item.heure_debut || item.type }}
                                        </p>
                                    </div>
                                    <p v-if="day.count > 2" class="text-xs text-center"
                                       :class="day.is_today ? 'text-gray-500' : 'text-gray-400'">
                                        +{{ day.count - 2 }}
                                    </p>
                                </div>

                                <div v-else class="flex-1 flex items-center justify-center">
                                    <span class="text-xs" :class="day.is_today ? 'text-gray-600' : 'text-gray-300'">—</span>
                                </div>
                            </div>
                        </div>
                    </div> -->

                    <!-- Tableau avec onglets -->
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <div class="flex items-center gap-1 mb-5">
                            <button v-for="tab in tabs" :key="tab.key"
                                    @click="activeTab = tab.key"
                                    class="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                                    :class="activeTab === tab.key
                                        ? 'bg-gray-900 text-white'
                                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'">
                                {{ tab.label }}
                                <span v-if="!loading && tab.count > 0"
                                      class="ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-bold"
                                      :class="activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'">
                                    {{ tab.count }}
                                </span>
                            </button>
                        </div>

                        <!-- A assigner -->
                        <div v-show="activeTab === 'assign'">
                            <div v-if="loading" class="space-y-2">
                                <div v-for="i in 5" :key="i" class="h-12 bg-gray-100 rounded-xl"></div>
                            </div>
                            <p v-else-if="d.to_assign.length === 0"
                               class="py-10 text-center text-sm text-gray-400">
                                Toutes les interventions sont assignées ✓
                            </p>
                            <div v-else class="space-y-px">
                                <div v-for="item in d.to_assign" :key="item.id"
                                     @click="$router.push(`/app/interventions?intervention_id=${item.id}`)"
                                     class="flex items-center justify-between px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group">
                                    <div class="flex items-center gap-3 min-w-0">
                                        <div class="w-2 h-2 rounded-full bg-amber-400 shrink-0"></div>
                                        <div class="min-w-0">
                                            <p class="text-sm font-medium text-gray-800 truncate">{{ item.client }}</p>
                                            <p class="text-xs text-gray-400">{{ item.date }} · {{ item.type }}</p>
                                        </div>
                                    </div>
                                    <span class="text-xs text-gray-300 group-hover:text-amber-500 font-medium ml-3 shrink-0 transition-colors">
                                        Assigner →
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- En retard -->
                        <div v-show="activeTab === 'overdue'">
                            <div v-if="loading" class="space-y-2">
                                <div v-for="i in 5" :key="i" class="h-12 bg-gray-100 rounded-xl"></div>
                            </div>
                            <p v-else-if="d.overdue_list.length === 0"
                               class="py-10 text-center text-sm text-gray-400">
                                Aucune intervention en retard ✓
                            </p>
                            <div v-else class="space-y-px">
                                <div v-for="item in d.overdue_list" :key="item.id"
                                     @click="$router.push(`/app/interventions?intervention_id=${item.id}`)"
                                     class="flex items-center justify-between px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group">
                                    <div class="flex items-center gap-3 min-w-0">
                                        <span class="text-xs font-bold tabular-nums shrink-0 w-8 text-right"
                                              :class="item.retard_jours > 7 ? 'text-red-500' : 'text-orange-400'">
                                            {{ item.retard_jours }}j
                                        </span>
                                        <div class="min-w-0">
                                            <p class="text-sm font-medium text-gray-800 truncate">{{ item.client }}</p>
                                            <p class="text-xs text-gray-400">
                                                {{ item.type }}<span v-if="item.technicien"> · {{ item.technicien }}</span>
                                            </p>
                                        </div>
                                    </div>
                                    <span class="text-xs text-gray-300 group-hover:text-red-400 font-medium ml-3 shrink-0 transition-colors">
                                        Traiter →
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Col droite 1/3 -->
                <div class="space-y-4">

                    <!-- Interventions du jour -->
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <h2 class="text-base font-semibold text-gray-900 mb-4">Aujourd'hui</h2>

                        <div v-if="loading" class="space-y-4">
                            <div v-for="i in 4" :key="i" class="flex gap-3">
                                <div class="w-8 h-8 rounded-full bg-gray-200 shrink-0"></div>
                                <div class="flex-1 space-y-2">
                                    <div class="h-3 bg-gray-200 rounded w-3/4"></div>
                                    <div class="h-3 bg-gray-100 rounded w-1/2"></div>
                                </div>
                            </div>
                        </div>

                        <p v-else-if="todayInterventions.length === 0"
                           class="py-8 text-center text-sm text-gray-400">
                            Aucune intervention aujourd'hui
                        </p>

                        <div v-else class="space-y-3">
                            <div v-for="item in todayInterventions" :key="item.id"
                                 @click="$router.push(`/app/interventions?intervention_id=${item.id}`)"
                                 class="flex items-start gap-3 cursor-pointer group">
                                <div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-white"
                                     :style="{ background: statusHex(item.status.color) }">
                                    {{ item.client.charAt(0).toUpperCase() }}
                                </div>
                                <div class="flex-1 min-w-0 pt-0.5">
                                    <p class="text-sm font-medium text-gray-800 truncate group-hover:text-gray-900">
                                        {{ item.client }}
                                    </p>
                                    <p class="text-xs text-gray-400">{{ item.type }}</p>
                                    <div class="flex items-center gap-1.5 mt-1">
                                        <span class="inline-block w-1.5 h-1.5 rounded-full"
                                              :style="{ background: statusHex(item.status.color) }"></span>
                                        <span class="text-xs text-gray-400">{{ item.status.label }}</span>
                                        <span v-if="item.heure_debut" class="text-xs text-gray-300">· {{ item.heure_debut }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Répartition par type -->
                    <div v-if="!loading && d.by_type && d.by_type.length > 0"
                         class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <h2 class="text-base font-semibold text-gray-900 mb-4">Par type — semaine</h2>
                        <div class="space-y-3">
                            <div v-for="item in d.by_type.slice(0, 5)" :key="item.type"
                                 class="flex items-center gap-3">
                                <span class="text-xs text-gray-500 w-24 shrink-0 truncate">{{ item.type }}</span>
                                <div class="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                    <div class="h-1.5 rounded-full bg-gray-800 transition-all duration-700"
                                         :style="{ width: barWidth(item.count) }"></div>
                                </div>
                                <span class="text-xs font-semibold text-gray-600 w-4 text-right shrink-0">
                                    {{ item.count }}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

        </app-template>
    </div>
</template>

<script>
export default {
    name: "HomeDashboard",

    data() {
        return {
            loading: true,
            activeTab: 'assign',
            d: {
                kpis: {
                    today:      { count: 0, delta: 0 },
                    week:       { count: 0 },
                    unassigned: { count: 0 },
                    overdue:    { count: 0 },
                },
                planning:     [],
                to_assign:    [],
                overdue_list: [],
                by_type:      [],
            },
        };
    },

    computed: {
        todayLabel() {
            return new Date().toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'long', year: 'numeric',
            });
        },

        kpis() {
            const k = this.d.kpis;
            const delta = k.today.delta;
            return [
                {
                    key: 'today', label: "Aujourd'hui", value: k.today.count,
                    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
                    iconBg: 'bg-blue-50', iconColor: 'text-blue-500',
                    sub: delta !== 0 ? (delta > 0 ? `+${delta} hier` : `${delta} hier`) : null,
                    subColor: delta >= 0 ? 'text-green-500' : 'text-red-400',
                },
                {
                    key: 'week', label: 'Cette semaine', value: k.week.count,
                    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
                    iconBg: 'bg-indigo-50', iconColor: 'text-indigo-500',
                    sub: 'interventions', subColor: 'text-gray-400',
                },
                {
                    key: 'unassigned', label: 'Sans technicien', value: k.unassigned.count,
                    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
                    iconBg: k.unassigned.count > 0 ? 'bg-amber-50' : 'bg-gray-50',
                    iconColor: k.unassigned.count > 0 ? 'text-amber-500' : 'text-gray-400',
                    sub: k.unassigned.count > 0 ? 'a assigner' : 'RAS',
                    subColor: k.unassigned.count > 0 ? 'text-amber-500' : 'text-gray-400',
                },
                {
                    key: 'overdue', label: 'En retard', value: k.overdue.count,
                    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                    iconBg: k.overdue.count > 0 ? 'bg-red-50' : 'bg-gray-50',
                    iconColor: k.overdue.count > 0 ? 'text-red-500' : 'text-gray-400',
                    sub: k.overdue.count > 0 ? 'non cloturees' : 'RAS',
                    subColor: k.overdue.count > 0 ? 'text-red-400' : 'text-gray-400',
                },
            ];
        },

        tabs() {
            return [
                { key: 'assign',  label: 'A assigner', count: this.d.kpis.unassigned?.count ?? 0 },
                { key: 'overdue', label: 'En retard',  count: this.d.kpis.overdue?.count ?? 0 },
            ];
        },

        todayInterventions() {
            if (!this.d.planning?.length) return [];
            const today = this.d.planning.find(d => d.is_today);
            return today ? today.interventions : [];
        },

        maxTypeCount() {
            if (!this.d.by_type?.length) return 1;
            return Math.max(...this.d.by_type.map(t => t.count));
        },
    },

    async mounted() {
        this.loadDashboard();
    },

    methods: {
        async loadDashboard() {
            this.loading = true;
            try {
                const res = await this.$request.get('/home-dashboard');
                this.d = res;
            } catch (e) {
                console.error('Erreur dashboard', e);
            } finally {
                this.loading = false;
            }
        },

        barWidth(count) {
            return `${Math.round((count / this.maxTypeCount) * 100)}%`;
        },

        statusHex(color) {
            const map = {
                red: '#ef4444', green: '#22c55e', yellow: '#f59e0b',
                blue: '#3b82f6', orange: '#f97316', gray: '#d1d5db',
            };
            return map[color] || map.gray;
        },
    },
};
</script>